import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@ibsys/kit';
import { format, startOfMonth } from 'date-fns';
import { useMissellingData } from '@/entities/dashboards';
import { PageContainer } from '@/shared/ui';
import { BarChartCard } from '@/widgets/BarChart';
import { MissellingFilters } from './MissellingFilters';
import { ColumnChartCard } from './parts/ColumnChartCard';
import { ProductReasonsRow } from './parts/ProductReasonsRow';

/**
 * Страница «Дашборд мисселинга РГС Жизнь».
 * Аналитика по фактам мисселинга в разговорах с «ООО Росгосстрах Жизнь»:
 * Ряд 1 — динамика по месяцам и ТОП-5 регионов; затем динамический набор
 * рядов «ТОП причин мисселинга» с разбивкой по продуктам.
 */
export const MissellingDashboardPage: FC = () => {
  const { t } = useTranslation('missellingDashboard');

  const [dateFrom, setDateFrom] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [selectedRefusalReasons, setSelectedRefusalReasons] = useState<string[]>([]);
  const [selectedMissellingTypes, setSelectedMissellingTypes] = useState<string[]>([]);
  const [selectedProductNames, setSelectedProductNames] = useState<string[]>([]);

  const params = useMemo(
    () => ({
      'date-from': dateFrom || null,
      'date-to': dateTo || null,
      'refusal-reasons': selectedRefusalReasons.length ? selectedRefusalReasons : undefined,
      'misselling-types': selectedMissellingTypes.length ? selectedMissellingTypes : undefined,
      'product-names': selectedProductNames.length ? selectedProductNames : undefined,
    }),
    [dateFrom, dateTo, selectedRefusalReasons, selectedMissellingTypes, selectedProductNames],
  );

  const { data, isLoading, isFetching } = useMissellingData(params);

  const dynamics = data?.dynamics ?? [];
  const topRegions = data?.topRegions ?? [];
  const productBreakdown = data?.productBreakdown ?? [];
  const filterOptions = data?.filterOptions;

  // Максимумы для нормировки полос Ряда 1
  const dynamicsMax = useMemo(() => Math.max(...dynamics.map((dynamicsItem) => dynamicsItem.count), 1), [dynamics]);
  const regionsMax = useMemo(() => Math.max(...topRegions.map((regionItem) => regionItem.count), 1), [topRegions]);

  const handleReset = useCallback(() => {
    setDateFrom(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
    setDateTo(format(new Date(), 'yyyy-MM-dd'));
    setSelectedRefusalReasons([]);
    setSelectedMissellingTypes([]);
    setSelectedProductNames([]);
  }, []);

  if (isLoading) {
    return (
      <PageContainer limitedWidth={false}>
        <div className="flex justify-center items-center h-64">
          <Spinner className="size-8" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer limitedWidth={true}>
      <div className="h-full w-full pb-4 flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          {isFetching && !isLoading && <Spinner className="size-4" />}
        </div>

        <MissellingFilters
          refusalReasons={filterOptions?.refusalReasons ?? []}
          missellingTypes={filterOptions?.missellingTypes ?? []}
          productNames={filterOptions?.productNames ?? []}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedRefusalReasons={selectedRefusalReasons}
          selectedMissellingTypes={selectedMissellingTypes}
          selectedProductNames={selectedProductNames}
          onChangeDateFrom={setDateFrom}
          onChangeDateTo={setDateTo}
          onChangeRefusalReasons={setSelectedRefusalReasons}
          onChangeMissellingTypes={setSelectedMissellingTypes}
          onChangeProductNames={setSelectedProductNames}
          onReset={handleReset}
        />

        {/* Ряд 1: Динамика (вертикальные столбики) | ТОП-5 регионов */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ColumnChartCard
            title={t('charts.dynamics')}
            items={dynamics.map((dynamicsItem) => ({
              label: dynamicsItem.month,
              value: dynamicsItem.count,
            }))}
            maxValue={dynamicsMax}
          />
          <BarChartCard
            title={t('charts.topRegions')}
            items={topRegions.map((regionItem) => ({
              label: regionItem.name,
              value: regionItem.count,
            }))}
            maxValue={regionsMax}
            format="integer"
            labelWidth="wide"
          />
        </div>

        {/* Динамические ряды «ТОП причин мисселинга по продукту» */}
        {productBreakdown.map((productItem) => (
          <ProductReasonsRow
            key={productItem.productName}
            productName={productItem.productName}
            topReasonsByAmount={productItem.topReasonsByAmount}
            topReasonsByCount={productItem.topReasonsByCount}
          />
        ))}
      </div>
    </PageContainer>
  );
};
